import { icon, usePrevious } from '@jupiterone/jupiter-web-toolkit';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import j1ToVisConverter from './converter/j1ToVisConverter';
import GraphView from './GraphView';
import {
  GraphEdge,
  GraphVertex,
  GraphViewType,
  NodePositionMap,
  SelectedNode,
  VisEdge,
  VisNode,
} from './types';
import getUnrelatedNodes from './utils/getUnrelatedNodes';

const filterByUnique = (value: any, index: number, self: any) => {
  return value && self.indexOf(value) === index;
};
const loadIcons = async (pendingIcons: string[]) => {
  await Promise.all(pendingIcons.map(icon.load));
};

interface Props {
  vertices: GraphVertex[];
  edges: GraphEdge[];
  nodePositions?: NodePositionMap;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  onSelectNode: (node: SelectedNode) => void;
  onSelectEdge: (id: string | null) => void;
  graphType: GraphViewType;
  onUpdateNodePositions?: (nodes: SelectedNode[]) => void;
  moveAllConnectedNodes: boolean;
}

const GraphInterface = memo(
  ({
    vertices,
    edges: initialEdges,
    selectedEdgeId,
    selectedNodeId,
    nodePositions,
    onSelectEdge,
    onSelectNode,
    graphType,
    onUpdateNodePositions,
    moveAllConnectedNodes,
  }: Props) => {
    const previousGraphType = usePrevious<GraphViewType>(graphType);
    const [pendingIcons, setPendingIcons] = useState<string[]>([]);
    const reloading = useBooleanState(false);
    const [{ nodes, edges }, setNodesAndEdges] = useState<{
      nodes: VisNode[];
      edges: VisEdge[];
    }>({ nodes: [], edges: [] });
    useEffect(() => {
      if (previousGraphType && previousGraphType !== graphType) {
        reloading.setTrue();
        setTimeout(reloading.setFalse);
      }
    }, [graphType]);

    const getPendingIcons = useCallback((data: { nodes: VisNode[] }) => {
      const uniqueIconPaths: string[] = data.nodes
        .map((n) => n.iconPath)
        .filter(filterByUnique);
      const unloadedIcons = icon.getUnloadedPaths(uniqueIconPaths);
      if (unloadedIcons.length) {
        setPendingIcons(unloadedIcons);
      }
    }, []);

    useEffect(() => {
      if (!pendingIcons.length) {
        const result = j1ToVisConverter(vertices, initialEdges, {
          selectedNodeId,
          selectedEdgeId,
          unrelatedNodeIds: selectedNodeId
            ? getUnrelatedNodes(selectedNodeId, {
                vertices,
                edges: initialEdges,
              })
            : undefined,
        });
        setNodesAndEdges(result);
        getPendingIcons(result);
      }
    }, [vertices, initialEdges, selectedEdgeId, selectedNodeId, pendingIcons]);

    useEffect(() => {
      if (pendingIcons.length) {
        loadIcons(pendingIcons).then(() => setPendingIcons([]));
      }
    }, [pendingIcons]);

    const handleSelectNodePosition = useCallback(
      (x: number, y: number) =>
        onSelectNode({ id: selectedNodeId, position: [x, y] }),
      [selectedNodeId]
    );

    return reloading.state ? (
      <></>
    ) : (
      <GraphView
        nodes={nodes}
        edges={edges}
        onSelectEdge={onSelectEdge}
        onSelectNode={onSelectNode}
        graphType={graphType}
        nodePositions={nodePositions}
        selectedNodeId={selectedNodeId}
        onSelectedNodePosition={handleSelectNodePosition}
        onUpdateNodePositions={onUpdateNodePositions}
        moveAllConnectedNodes={moveAllConnectedNodes}
      />
    );
  }
);

export default GraphInterface;
